import * as React from 'react';
import { BaSeI18nConsumer } from '../../contexts/i18n';
import { BaSeButtonElement } from '../../elements/button-element';
import { useOutsideEvent } from '../../hooks/outside-event';
import { BaSeTheme } from '../../theme';
import { ThemeColorValue } from '../../theme/theme-interface';
import {
  ColorMapper,
  ColorMapperType,
} from '../../utils/color-transformation/color-mapper';
import { objectNotEmpty } from '../../utils/object-utils';
import { AccordionOption, BaSeAccordion } from '../accordion/accordion';
import { BaSeTooltip, TooltipProps } from '../alerts/tooltip/tooltip';
import {
  BaSeArrowButton,
  BaSeArrowButtonProps,
} from '../button/arrow-button/arrow-button';
import { ButtonProps } from '../button/button-props';
import { BaSeButton } from '../button/button/button';
import {
  BaSeShapeButton,
  ShapeButtonProps,
} from '../button/shape-button/shape-button';
import { BaSeIcon } from '../image/icon';
import { BaSeParagraph } from '../typography/paragraph/paragraph';
import { BaSeSmall1 } from '../typography/small/small1';
import { BaSeSmall2 } from '../typography/small/small2';
import {
  ActionItemGroupProps,
  ActionItemMoreInfoPosition,
  ActionItemMoreInfoProps,
  BaSeItemProps,
  DisplayButton,
  ItemButtonType,
  PositionRelativeType,
  SingleActionItemInterface,
  SingleItemProps,
  SingleItemStyledProps,
} from './action-item-props';
import {
  ActionItemAccordionContent,
  ActionItemContainer,
  ActionItemGroup,
  ActionItemGroupDivider,
  ActionItemGroupItems,
  ActionItemGroupTitle,
  ActionItemHeaderInfo,
  ActionItemHeaderInfoContent,
  ActionItemListWrapper,
  ActionItemSingleItem,
  ActionItemSingleLink,
  ActionItemTextContent,
  ActionItemTitleAccordionContainer,
  ActionItemWrapper,
} from './action-item-styled';

const NEGATIVE_INDEX = -99;

type ActionItemKeyboardEvent = React.KeyboardEvent<
  HTMLButtonElement | HTMLAnchorElement | HTMLDivElement
>;

const handleVerticalArrowNavigation = (
  event: ActionItemKeyboardEvent,
  handlers: {
    onArrowDown: () => void;
    onArrowUp: () => void;
  },
) => {
  if (event.key === 'ArrowDown') {
    event.preventDefault();
    event.stopPropagation();
    handlers.onArrowDown();
  }

  if (event.key === 'ArrowUp') {
    event.preventDefault();
    event.stopPropagation();
    handlers.onArrowUp();
  }
};

const DEFAULT_ITEM_COLORS = {
  unselected: {
    textColor: ColorMapper.default.foreground,
    backgroundColor: BaSeTheme.colors.defaultColors.white,
    hoverBackgroundColor: ColorMapper.default.hover.defaultLight,
  },
  selected: {
    textColor: BaSeTheme.colors.defaultColors.white,
    backgroundColor: ColorMapper.default.foreground,
    hoverBackgroundColor: ColorMapper.default.hover.defaultDark,
  },
  disabled: {
    textColor: ColorMapper.disabled.foreground,
    backgroundColor: ColorMapper.disabled.background,
    disabled: true,
  },
};

interface SingleActionItemTitleContentProps {
  item: SingleItemProps;
  customColor: SingleItemStyledProps;
  createDisableIconColor: () => ThemeColorValue;
}

const SingleActionItemTitleContent: React.FC<
  SingleActionItemTitleContentProps
> = ({ createDisableIconColor, customColor, item }) => {
  return (
    <>
      {item?.icon && (
        <BaSeI18nConsumer>
          {({ getMessage }) => (
            <BaSeIcon
              description={getMessage('icon.label', item.text)}
              name={item.icon as string}
              color={createDisableIconColor()}
            />
          )}
        </BaSeI18nConsumer>
      )}
      <ActionItemTextContent>
        <BaSeParagraph color={customColor?.textColor}>
          {item.text}
        </BaSeParagraph>
        {item?.subLabel && (
          <BaSeSmall1 color={BaSeTheme.colors.institucionais.cinzaSebrae45}>
            {item.subLabel}
          </BaSeSmall1>
        )}
      </ActionItemTextContent>
    </>
  );
};

interface SingleActionItemTitleProps {
  customColor: SingleItemStyledProps;
  item: SingleItemProps;
  index: number;
  indented?: boolean;

  callback?: () => void;
  updateCurrent: (index: number) => void;
  createDisableIconColor: () => ThemeColorValue;
  selfRef: (node: HTMLButtonElement | HTMLAnchorElement | null) => void;
}

const SingleActionItemTitle: React.FC<SingleActionItemTitleProps> = ({
  customColor,
  item,
  selfRef,
  callback,
  updateCurrent,
  createDisableIconColor,
  index,
  indented = false,
}) => {
  const linkProps = React.useMemo(() => {
    if (!item?.link) {
      return null;
    }
    return typeof item.link === 'string' ? { href: item.link } : item.link;
  }, [item]);

  const handleClick = React.useCallback(() => {
    if (!item?.isDisabled) {
      item.callback?.();
      callback?.();
    }
  }, [item, callback]);

  const handleFocus = React.useCallback(
    (
      event: React.FocusEvent<
        HTMLButtonElement | HTMLAnchorElement | HTMLDivElement
      >,
    ) => {
      event.preventDefault();
      event.stopPropagation();
      updateCurrent(index);
    },
    [index, updateCurrent],
  );

  const handleKeyDown = React.useCallback(
    (event: ActionItemKeyboardEvent) => {
      handleVerticalArrowNavigation(event, {
        onArrowDown: () => updateCurrent(index + 1),
        onArrowUp: () => updateCurrent(index - 1),
      });
    },
    [index, updateCurrent],
  );

  const disableTabIndex = React.useCallback(() => {
    return item?.isDisabled ? { tabIndex: -1 } : {};
  }, [item]);

  if (linkProps && !item?.isDisabled) {
    return (
      <ActionItemSingleLink
        ref={selfRef}
        onFocus={handleFocus}
        onKeyDown={handleKeyDown}
        onClick={handleClick}
        customColor={customColor}
        indented={indented}
        aria-disabled={item?.isDisabled}
        {...disableTabIndex()}
        {...linkProps}
      >
        <SingleActionItemTitleContent
          createDisableIconColor={createDisableIconColor}
          customColor={customColor}
          item={item}
        />
      </ActionItemSingleLink>
    );
  }

  return (
    <ActionItemSingleItem
      ref={selfRef}
      onFocus={handleFocus}
      onKeyDown={handleKeyDown}
      onClick={handleClick}
      customColor={customColor}
      indented={indented}
      aria-disabled={item?.isDisabled}
      {...disableTabIndex()}
    >
      <SingleActionItemTitleContent
        createDisableIconColor={createDisableIconColor}
        customColor={customColor}
        item={item}
      />
    </ActionItemSingleItem>
  );
};

const SingleActionItem: React.FC<SingleActionItemInterface> = ({
  item,
  index,
  opened,
  hasFocus,
  callback,
  updateCurrent,
  subitems,
  onClick,
  indented = false,
}) => {
  const [currentIndex, setCurrentIndex] =
    React.useState<number>(NEGATIVE_INDEX);
  const [menuOpen, setMenuOpen] = React.useState<boolean>(false);

  const selfRef = React.useRef<
    HTMLButtonElement | HTMLAnchorElement | BaSeButtonElement | null
  >(null);

  const setSelfRef = React.useCallback(
    (
      node: HTMLButtonElement | HTMLAnchorElement | BaSeButtonElement | null,
    ) => {
      selfRef.current = node;
    },
    [],
  );

  const getIconColor = React.useCallback(() => {
    if (!item?.color) {
      return ColorMapper.default.foreground;
    }
    if (ColorMapper.isValidType(item?.color ?? '')) {
      const colorMapper = ColorMapper[item.color] as ColorMapperType;
      if ('foreground' in colorMapper) {
        return colorMapper.foreground;
      }
    }
    if (ColorMapper.isValidColor(item?.color ?? '')) {
      return item.color;
    }
    return ColorMapper.default.foreground;
  }, [item]);

  const createDisableIconColor = React.useCallback(() => {
    if (item?.isDisabled) {
      return BaSeTheme.colors.institucionais.cinzaSebrae75;
    }
    return item.isSelected
      ? BaSeTheme.colors.defaultColors.white
      : getIconColor();
  }, [item, getIconColor]);

  const getItemColor = React.useCallback((): SingleItemStyledProps => {
    if (item?.isDisabled) {
      return DEFAULT_ITEM_COLORS.disabled;
    }
    const selected = item?.isSelected ? 'selected' : 'unselected';
    if (ColorMapper.isValidType(item?.color ?? '')) {
      return {
        unselected: {
          textColor: ColorMapper[item.color as string].foreground as string,
          backgroundColor: BaSeTheme.colors.defaultColors.white,
          hoverBackgroundColor: ColorMapper[item.color as string].hover
            .defaultLight as string,
        },
        selected: {
          backgroundColor: ColorMapper[item.color as string]
            .foreground as string,
          textColor: BaSeTheme.colors.defaultColors.white,
          hoverBackgroundColor: ColorMapper[item.color as string].hover
            .defaultDark as string,
        },
      }[selected];
    }
    if (ColorMapper.isValidColor(item?.color ?? '')) {
      return {
        unselected: {
          textColor: item?.color as string,
          backgroundColor: BaSeTheme.colors.defaultColors.white,
          hoverBackgroundColor: ColorMapper.light.hover.create(
            item.color as string,
          ),
        },
        selected: {
          backgroundColor: item?.color,
          textColor: BaSeTheme.colors.defaultColors.white,
          hoverBackgroundColor: ColorMapper.dark.hover.create(
            item.color as string,
          ),
        },
      }[selected];
    }
    return DEFAULT_ITEM_COLORS[selected];
  }, [item]);

  React.useEffect(() => {
    if (hasFocus && opened && selfRef?.current) {
      selfRef?.current?.focus();
    }
  }, [hasFocus, opened]);

  const customColor = getItemColor();

  const returnDefault = React.useCallback(() => {
    onClick?.();
    setCurrentIndex(NEGATIVE_INDEX);
    setMenuOpen(false);
  }, [onClick]);

  const handleSubitemsOpen = React.useCallback(
    (nextOpen: boolean, setOpen: (value: boolean) => void) => {
      setOpen(nextOpen);
      setMenuOpen(nextOpen);
      setCurrentIndex(nextOpen ? 0 : NEGATIVE_INDEX);
    },
    [],
  );

  const handleKeyDown = React.useCallback((event: ActionItemKeyboardEvent) => {
    handleVerticalArrowNavigation(event, {
      onArrowDown: () =>
        setCurrentIndex((prev) => (prev === NEGATIVE_INDEX ? 0 : prev + 1)),
      onArrowUp: () =>
        setCurrentIndex((prev) => (prev === NEGATIVE_INDEX ? 0 : prev - 1)),
    });
  }, []);

  const handleAccordionTriggerKeyDown = React.useCallback(
    (
      event: ActionItemKeyboardEvent,
      open: boolean,
      setOpen: (value: boolean) => void,
    ) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        event.stopPropagation();
        if (!item?.isDisabled) {
          handleSubitemsOpen(!open, setOpen);
        }
        return;
      }

      handleVerticalArrowNavigation(event, {
        onArrowDown: () => {
          if (open && item.subitems?.length) {
            setCurrentIndex(0);
            setMenuOpen(true);
            return;
          }
          updateCurrent(index + 1);
        },
        onArrowUp: () => {
          if (open && item.subitems?.length) {
            setCurrentIndex(item.subitems.length - 1);
            setMenuOpen(true);
            return;
          }
          updateCurrent(index - 1);
        },
      });
    },
    [handleSubitemsOpen, index, item, updateCurrent],
  );

  const options = React.useMemo<AccordionOption[]>(
    () => [
      {
        title: ({ setOpen, open }) => (
          <ActionItemTitleAccordionContainer
            onClick={(event: React.MouseEvent<HTMLDivElement>) => {
              event.stopPropagation();
              if (!item?.isDisabled) {
                handleSubitemsOpen(!open, setOpen);
              }
            }}
            customColor={customColor}
            indented={indented}
          >
            <SingleActionItemTitleContent
              createDisableIconColor={createDisableIconColor}
              customColor={customColor}
              item={item}
            />
          </ActionItemTitleAccordionContainer>
        ),
        renderOpenCloseButton: ({ open, setOpen, color }) => (
          <div
            onFocus={(event: React.FocusEvent<HTMLDivElement>) => {
              event.preventDefault();
              event.stopPropagation();
              updateCurrent(index);
            }}
            onKeyDownCapture={(event: React.KeyboardEvent<HTMLDivElement>) =>
              handleAccordionTriggerKeyDown(event, open, setOpen)
            }
          >
            <BaSeShapeButton
              nameIcon="angle-down"
              size="small"
              sizeIcon="small"
              type="tertiary"
              color={color}
              ref={setSelfRef}
              isDisabled={item?.isDisabled}
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                if (!item?.isDisabled) {
                  handleSubitemsOpen(!open, setOpen);
                }
              }}
            />
          </div>
        ),
        content: (
          <ActionItemListWrapper onKeyDown={handleKeyDown}>
            <ActionItemAccordionContent>
              {item.subitems?.map((subitem, key) => (
                <SingleActionItem
                  callback={returnDefault}
                  item={subitem}
                  key={key}
                  index={key}
                  indented={true}
                  opened={menuOpen}
                  hasFocus={key === currentIndex}
                  updateCurrent={setCurrentIndex}
                  subitems={subitem.subitems}
                  onClick={onClick}
                />
              ))}
            </ActionItemAccordionContent>
          </ActionItemListWrapper>
        ),
        noPaddingContent: true,
        noPaddingTitle: true,
      },
    ],
    [
      createDisableIconColor,
      currentIndex,
      customColor,
      handleAccordionTriggerKeyDown,
      handleSubitemsOpen,
      handleKeyDown,
      indented,
      index,
      item,
      menuOpen,
      onClick,
      setSelfRef,
      updateCurrent,
      returnDefault,
    ],
  );

  return subitems ? (
    <BaSeAccordion
      backgroundContentColor={BaSeTheme.colors.defaultColors.white}
      titleButtonIconSize="small"
      titleButtonSize="small"
      hasContentBorder={false}
      hasTitleBorder={false}
      shadow={BaSeTheme.shadows.profundidade0}
      multipleOpen={true}
      options={options}
    />
  ) : (
    <SingleActionItemTitle
      customColor={customColor}
      selfRef={setSelfRef}
      item={item}
      callback={callback}
      updateCurrent={updateCurrent}
      createDisableIconColor={createDisableIconColor}
      index={index}
      indented={indented}
    />
  );
};

export const BaSeActionItem: React.FC<BaSeItemProps> = ({
  icon = 'ellipsis-v',
  descriptionIcon,
  type = 'tertiary',
  size = 'extra-small',
  sizeIcon = 'medium',
  color = BaSeTheme.colors.institucionais.azulSebrae36,
  items = [],
  groups = [],
  moreInfo,
  isDisabled = false,
  closeOnAction = true,
  minWidth = 164,
  open = false,
  noIcon = false,
  trackPosition = false,
  position = 'top-left',
  tooltip = {},
  buttonType = 'base-shape-button',
  customButtonProps = {},
  onClose,
  onOpen,
}) => {
  const [menuOpen, setMenuOpen] = React.useState<boolean>(false);
  const menuRef = React.useRef<HTMLDivElement>(null);
  const [currentIndex, setCurrentIndex] =
    React.useState<number>(NEGATIVE_INDEX);
  const [styles, setStyles] = React.useState<string>('');

  const [isTooltipOpen, setIsTooltipOpen] = React.useState(false);

  const hasGroups = React.useMemo(() => groups.length > 0, [groups.length]);

  const normalizedGroups = React.useMemo<ActionItemGroupProps[]>(
    () => (hasGroups ? groups : items.length > 0 ? [{ items }] : []),
    [groups, hasGroups, items],
  );

  const flatItems = React.useMemo(
    () =>
      normalizedGroups.reduce<SingleItemProps[]>(
        (acc, group) => [...acc, ...group.items],
        [],
      ),
    [normalizedGroups],
  );

  const moreInfoPosition = React.useMemo<ActionItemMoreInfoPosition>(
    () => moreInfo?.position ?? 'header',
    [moreInfo],
  );

  const renderActionItemMoreInfo = React.useMemo(() => {
    if (!moreInfo?.content) {
      return null;
    }

    if (typeof moreInfo.content === 'function') {
      return moreInfo.content();
    }

    return (
      <BaSeActionItemMoreInfo
        {...moreInfo.content}
        position={moreInfoPosition}
      />
    );
  }, [moreInfo, moreInfoPosition]);

  useOutsideEvent(menuRef, () => {
    setMenuOpen(false);
    onClose?.();
  });

  const handleButtonType = React.useCallback((): ItemButtonType => {
    if (!buttonType) {
      return 'base-shape-button';
    }
    const option: ItemButtonType[] = [
      'base-button',
      'base-shape-button',
      'base-arrow-button',
    ];
    return option.find((i) => i === buttonType) ?? 'base-shape-button';
  }, [buttonType]);

  const displayButton: DisplayButton = React.useCallback(() => {
    return {
      'base-button': (
        baseButtonProps: Partial<
          Omit<
            ButtonProps,
            | 'onClick'
            | 'onFocus'
            | 'onBlur'
            | 'isDisabled'
            | 'size'
            | 'color'
            | 'type'
          >
        >,
      ) => (
        <BaSeButton
          {...baseButtonProps}
          tooltip={tooltip}
          onClick={() => setMenuOpen((prev) => !prev)}
          isDisabled={isDisabled}
          size={
            'extra-small' === size || 'mega-small' === size ? 'small' : size
          }
          color={color}
          type={type}
          onFocus={undefined}
          onBlur={undefined}
        />
      ),
      'base-shape-button': (
        baseShapeButtonProps?: Partial<
          Omit<
            ShapeButtonProps,
            | 'tooltip'
            | 'isDisabled'
            | 'size'
            | 'sizeIcon'
            | 'color'
            | 'type'
            | 'nameIcon'
            | 'onClick'
            | 'onFocus'
            | 'onBlur'
          >
        >,
      ) => (
        <BaSeI18nConsumer>
          {({ getMessage }) => (
            <BaSeShapeButton
              {...baseShapeButtonProps}
              tooltip={tooltip}
              isDisabled={isDisabled}
              size={size}
              sizeIcon={sizeIcon}
              color={color}
              type={type}
              nameIcon={icon}
              descriptionIcon={
                descriptionIcon?.trim() ||
                getMessage('actionItem.iconDescription')
              }
              onClick={() => setMenuOpen((prev) => !prev)}
              onFocus={undefined}
              onBlur={undefined}
            />
          )}
        </BaSeI18nConsumer>
      ),
      'base-arrow-button': (baseArrowButtonProps: BaSeArrowButtonProps) => (
        <BaSeArrowButton
          {...baseArrowButtonProps}
          tooltip={tooltip}
          isDisabled={isDisabled}
          color={color}
          type={type}
          onClick={() => setMenuOpen((prev) => !prev)}
          onFocus={undefined}
          onBlur={undefined}
        />
      ),
    };
  }, [color, descriptionIcon, icon, isDisabled, size, sizeIcon, tooltip, type]);

  const returnDefault = React.useCallback(() => {
    setCurrentIndex(NEGATIVE_INDEX);
    setMenuOpen(false);
    onClose?.();
  }, [onClose]);

  const handlePosition = React.useCallback((pos: PositionRelativeType) => {
    let styledPosition = '';
    styledPosition += pos.includes('top') ? 'top:100%;' : 'bottom: 100%;';
    styledPosition += pos.includes('left') ? 'left:0;' : 'right: 0;';
    return styledPosition;
  }, []);

  React.useEffect(() => {
    if (menuOpen) {
      onOpen?.();
      setTimeout(() => {
        setCurrentIndex(0);
      }, 200);
    } else {
      setStyles('');
      setCurrentIndex(NEGATIVE_INDEX);
    }
  }, [menuOpen, onOpen]);

  React.useEffect(() => {
    if (currentIndex < 0 && currentIndex !== NEGATIVE_INDEX) {
      setCurrentIndex(flatItems.length - 1);
    }
    if (currentIndex > flatItems.length - 1) {
      setCurrentIndex(0);
    }
  }, [currentIndex, flatItems.length]);

  React.useEffect(() => {
    if (noIcon) {
      setMenuOpen(open);
    }
  }, [open, noIcon]);

  React.useEffect(() => {
    if (trackPosition) {
      try {
        let style = '';
        const { bottom, right, width, height } =
          menuRef?.current?.getBoundingClientRect() as DOMRect;
        if (right && bottom && width && height && !styles) {
          const { innerWidth, innerHeight } = window as Window;
          if (height < innerHeight) {
            if (bottom > innerHeight) {
              style += 'bottom: 100%;';
            } else {
              style += 'top:100%;';
            }
          }
          if (width < innerWidth) {
            if (right > innerWidth) {
              style += 'right:0;';
            } else {
              style += 'left:0;';
            }
          }
          if (style === styles) {
            return;
          }
          setStyles(style);
        }
      } catch {
        setStyles('');
      }
    } else {
      if (position) {
        setStyles(handlePosition(position));
      }
    }
  }, [handlePosition, position, styles, trackPosition]);

  return (
    <ActionItemWrapper
      onMouseEnter={() => {
        setIsTooltipOpen(true);
      }}
      onMouseLeave={() => {
        setIsTooltipOpen(false);
      }}
      relative={!noIcon}
      ref={menuRef}
    >
      {!noIcon &&
        displayButton()[handleButtonType() as ItemButtonType](
          customButtonProps,
        )}
      {flatItems.length > 0 && !isDisabled && (
        <ActionItemContainer
          minWidth={minWidth}
          addStyles={styles}
          className={menuOpen ? 'BaSe--action-item-open' : ''}
        >
          {moreInfoPosition === 'header' ? renderActionItemMoreInfo : null}
          <ActionItemListWrapper>
            {normalizedGroups.map((group, groupIndex) => {
              let previousItemsCount = 0;
              for (let index = 0; index < groupIndex; index += 1) {
                previousItemsCount += normalizedGroups[index].items.length;
              }

              return (
                <React.Fragment key={`action-item-group-${groupIndex}`}>
                  {hasGroups && groupIndex > 0 ? (
                    <ActionItemGroupDivider />
                  ) : null}
                  <ActionItemGroup>
                    {group.title && (
                      <ActionItemGroupTitle>
                        <BaSeParagraph
                          color={BaSeTheme.colors.institucionais.cinzaSebrae60}
                        >
                          {group.title}
                        </BaSeParagraph>
                      </ActionItemGroupTitle>
                    )}
                    <ActionItemGroupItems>
                      {group.items.map((item, itemIndex) => {
                        const flatIndex = previousItemsCount + itemIndex;

                        return (
                          <SingleActionItem
                            callback={closeOnAction ? returnDefault : undefined}
                            item={item}
                            key={`${groupIndex}-${itemIndex}`}
                            index={flatIndex}
                            indented={hasGroups}
                            opened={menuOpen}
                            hasFocus={flatIndex === currentIndex}
                            updateCurrent={setCurrentIndex}
                            subitems={item.subitems}
                            onClick={closeOnAction ? returnDefault : undefined}
                          />
                        );
                      })}
                    </ActionItemGroupItems>
                  </ActionItemGroup>
                </React.Fragment>
              );
            })}
          </ActionItemListWrapper>
          {moreInfoPosition === 'footer' ? renderActionItemMoreInfo : null}
        </ActionItemContainer>
      )}
      {objectNotEmpty(tooltip) && !!noIcon && (
        <BaSeTooltip
          buddyRef={menuRef}
          open={isTooltipOpen}
          {...(tooltip as Omit<TooltipProps, 'open' | 'buddyRef'>)}
        />
      )}
    </ActionItemWrapper>
  );
};

BaSeActionItem.displayName = 'BaSeActionItem';

const BaSeActionItemMoreInfo: React.FC<{
  title: ActionItemMoreInfoProps['title'];
  subtitle: ActionItemMoreInfoProps['subtitle'];
  icon?: ActionItemMoreInfoProps['icon'];
  position: ActionItemMoreInfoPosition;
}> = ({ title, subtitle, icon = 'question-circle', position }) => {
  return (
    <ActionItemHeaderInfo position={position}>
      <ActionItemHeaderInfoContent>
        <BaSeSmall1 color={BaSeTheme.colors.institucionais.cinzaSebrae45}>
          {title}
        </BaSeSmall1>
        <BaSeSmall2 color={BaSeTheme.colors.institucionais.cinzaSebrae45}>
          {subtitle}
        </BaSeSmall2>
      </ActionItemHeaderInfoContent>
      <BaSeIcon
        name={icon}
        size={16}
        color={BaSeTheme.colors.institucionais.azulSebrae36}
        description={title}
      />
    </ActionItemHeaderInfo>
  );
};

BaSeActionItemMoreInfo.displayName = 'BaSeActionItemMoreInfo';
